"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../../../navigation/Layout/Layout";

import PharmacyHeader from "../../components/PharmacyHeader/PharmacyHeader";
import PharmacyList from "../../components/PharmacyList/PharmacyList";

import usePlaces from "../../hooks/usePlaces";
import { useTour } from "../../../appointments/hooks/useTour";
import { labTourSteps } from "./labTourConfig";

import "./Lab.css";

export default function Lab() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [shouldStartTour, setShouldStartTour] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  // For pagination display
  const [visibleCount, setVisibleCount] = useState(20); // ⬅️ first 20 items only

  const listRef = useRef(null);

  // ---- places store
  const {
    items,         // full list from backend
    loading,
    error,
    loadCategory,
    selectPlace,
  } = usePlaces();

  // Load initial category
  useEffect(() => {
    loadCategory("analysis_labs", { limit: 200, skip: 0 }); // get more data at once
  }, [loadCategory]);

  // Guided tour
  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "lab";

    if (hasGuide || hasTour) {
      setTimeout(() => {
        setShouldStartTour(true);
      }, 1000);
    }
  }, [searchParams]);

  useTour(shouldStartTour, labTourSteps);

  // Filter data locally
  const filtered = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const q = searchQuery.trim().toLowerCase();

    if (!q) {
      return list.slice(0, visibleCount); // ⬅️ Show only first 20 (or more if scrolled)
    }

    // When searching → always search the *full* dataset
    return list.filter(
      (lab) =>
        (lab.name || "").toLowerCase().includes(q) ||
        (lab.address || "").toLowerCase().includes(q) ||
        (lab.vicinity || "").toLowerCase().includes(q)
    );
  }, [items, searchQuery, visibleCount]);

  // Infinite scroll inside the list container
  useEffect(() => {
    const div = listRef.current;
    if (!div) return;

    const handleScroll = () => {
      if (loading) return;

      // when bottom reached
      if (div.scrollTop + div.clientHeight >= div.scrollHeight - 40) {
        setVisibleCount((prev) => prev + 20); // load 20 more
      }
    };

    div.addEventListener("scroll", handleScroll);
    return () => div.removeEventListener("scroll", handleScroll);
  }, [loading]);

  // Reset visible count when searching
  useEffect(() => {
    if (searchQuery.trim() !== "") {
      setVisibleCount(200); // show all search results
    } else {
      setVisibleCount(20); // reset when clearing search
    }
  }, [searchQuery]);

  return (
    <AppLayout>
      <main className="lab-main lab--listOnly">
        <div className="lab-container">

          <PharmacyHeader
	            title={t("locators.lab.title")}
	            subtitle={t("locators.lab.subtitle")}
            showTabs={false}
            enableFilters={false}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            activeTab="Laboratory"
            setActiveTab={() => {}}
          />

          <section className="lab-list-card">
	            <div className="lab-list-scroll" ref={listRef}>
	              {loading && (
	                <p className="lab-empty">{t("locators.lab.loading")}</p>
	              )}

	              {!loading && error && (
	                <p className="lab-empty">
	                  {t("locators.lab.failed", { error: String(error) })}
	                </p>
	              )}

	              {!loading && !error && filtered.length === 0 ? (
	                <p className="lab-empty">{t("locators.lab.empty")}</p>
	              ) : (
                <PharmacyList
                  pharmacies={filtered}
                  onPharmacyClick={(lab) => selectPlace(lab)}
                  selectedPharmacy={null}
                />
              )}
            </div>
          </section>

        </div>
      </main>
    </AppLayout>
  );
}
